import type { Project, Starring } from "@beacon/core/projects"
import { queryOptions } from "@tanstack/react-query"
import { call } from "@/administration/client.ts"
import { projectKeys } from "./client.ts"

/*
 * The Member's starred Projects, as the web API keeps them: theirs alone, kept with their account
 * so every device and session has them. Which starred Projects show is the web API's call: one they
 * can't read now, or one archived, isn't listed, though its star waits for it.
 */

/**
 * The Projects the Member starred that they can read now and that aren't archived. Under
 * `projectKeys.all`, so a change to any Project refreshes it too; `starred` is lower case, so no
 * Project's key meets it.
 */
export const starredProjectsQuery = queryOptions({
  queryKey: [...projectKeys.all, "starred"],
  queryFn: () => call<Project[]>("GET", "/starred-projects"),
})

/** Stars the Project `key` names for the Member, or unstars it. */
export const setStarred = (key: string, starred: boolean) =>
  call<Starring>(starred ? "PUT" : "DELETE", `/projects/${encodeURIComponent(key)}/star`)

/** The starred Projects with `project` starred or not, each once; the list passed in is kept. */
export function withStarred(
  starred: readonly Project[],
  project: Project,
  wanted: boolean,
): Project[] {
  const others = starred.filter(({ key }) => key !== project.key)
  return wanted ? [...others, project] : others
}

/** The keys of the starred Projects, none while they load. */
export const starredKeys = (starred: readonly Project[] | undefined) =>
  new Set((starred ?? []).map(({ key }) => key))

/**
 * What a Project's star is called: `label`, for a screen reader, names the Project and stays as it
 * is while the button says whether it is pressed; `tip` says what pressing it does now.
 */
export const describeStar = (project: Pick<Project, "name">, starred: boolean) => ({
  label: `Star ${project.name}`,
  tip: starred ? "Unstar" : "Star",
})
